import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface InboxFilterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (min: number | undefined, max: number | undefined) => void;
  onClear: () => void;
  provider: 'gmail' | 'outlook' | 'yahoo' | null;
  currentMin?: number | undefined;
  currentMax?: number | undefined;
}

const InboxFilterPopup: React.FC<InboxFilterPopupProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  provider,
  currentMin,
  currentMax,
}) => {
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setMinValue(currentMin?.toString() || '');
      setMaxValue(currentMax?.toString() || '');
    }
  }, [isOpen, currentMin, currentMax]);

  const handleApply = () => {
    // Trim whitespace and handle empty strings
    const trimmedMin = minValue.trim();
    const trimmedMax = maxValue.trim();
    
    // Convert to numbers, handling empty strings and invalid inputs
    // Explicitly check for empty string to allow 0 as a valid value
    const minNum = trimmedMin === '' ? undefined : Number(trimmedMin);
    const maxNum = trimmedMax === '' ? undefined : Number(trimmedMax);
    
    // Validate that inputs are valid numbers (but allow 0)
    if (trimmedMin !== '' && (isNaN(minNum!) || !isFinite(minNum!))) {
      alert('Please enter a valid number for minimum value');
      return;
    }
    
    if (trimmedMax !== '' && (isNaN(maxNum!) || !isFinite(maxNum!))) {
      alert('Please enter a valid number for maximum value');
      return;
    }
    
    // Explicitly handle 0 as a valid value (not undefined)
    const min = trimmedMin === '' ? undefined : minNum;
    const max = trimmedMax === '' ? undefined : maxNum;
    
    console.log('Popup handleApply - minValue:', minValue, 'maxValue:', maxValue);
    console.log('Popup handleApply - min:', min, 'max:', max, 'min type:', typeof min, 'max type:', typeof max);
    
    // Validate that min is not greater than max
    if (min !== undefined && max !== undefined && min > max) {
      alert('Minimum value cannot be greater than maximum value');
      return;
    }
    
    // Validate that values are between 0 and 100
    if ((min !== undefined && (min < 0 || min > 100)) || 
        (max !== undefined && (max < 0 || max > 100))) {
      alert('Values must be between 0 and 100');
      return;
    }
    
    onApply(min, max);
    onClose();
  };

  const handleClear = () => {
    setMinValue('');
    setMaxValue('');
    onClear();
  };

  const getProviderName = () => {
    switch (provider) {
      case 'gmail':
        return 'Combined Reputation Score';
      case 'outlook':
        return 'Combined Reputation Score';
      default:
        return 'Combined Reputation Score';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Filter {getProviderName()} Reputation Score
          </DialogTitle>
        </DialogHeader>
        
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="min-percentage">Minimum Combined Score</Label>
              <Input
                id="min-percentage"
                type="number"
                min="0"
                max="100"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                placeholder="0"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="max-percentage">Maximum Combined Score</Label>
              <Input
                id="max-percentage"
                type="number"
                min="0"
                max="100"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                placeholder="100"
                className="w-full"
              />
            </div>
            
            <div className="text-sm text-gray-500">
              Filter by combined Google + Microsoft reputation score (average). Enter values between 0 and 100. Leave empty to remove the filter.
            </div>
          </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClear}>
            Clear Filters
          </Button>
          <Button onClick={handleApply}>
            Apply Filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InboxFilterPopup;
